import { MigrationObjectType } from "../types/domains/accounting/accountingMigration";

export interface MigrationSyncFlags {
    syncGeneralLedger: boolean;
    syncCounterparties: boolean;
    syncAttachments: boolean | null;
    syncClasses: boolean | null;
    syncCatalogItems: boolean | null;
    syncInvoices: boolean | null;
    syncBills: boolean | null;
}

export const MIGRATION_OBJECT_TYPE_SYNC_FIELDS: Record<
    MigrationObjectType,
    keyof MigrationSyncFlags
> = {
    [MigrationObjectType.GENERAL_LEDGER]: "syncGeneralLedger",
    [MigrationObjectType.COUNTERPARTIES]: "syncCounterparties",
    [MigrationObjectType.ATTACHMENTS]: "syncAttachments",
    [MigrationObjectType.CLASSES]: "syncClasses",
    [MigrationObjectType.CATALOG_ITEMS]: "syncCatalogItems",
    [MigrationObjectType.INVOICES]: "syncInvoices",
    [MigrationObjectType.BILLS]: "syncBills",
};

export const MIGRATION_OBJECT_TYPES: MigrationObjectType[] =
    Object.values(MigrationObjectType);

export function isMigrationObjectTypeEnabled(
    flags: MigrationSyncFlags,
    objectType: MigrationObjectType,
): boolean {
    return flags[MIGRATION_OBJECT_TYPE_SYNC_FIELDS[objectType]] === true;
}

export function enabledMigrationObjectTypes(
    flags: MigrationSyncFlags,
): MigrationObjectType[] {
    return MIGRATION_OBJECT_TYPES.filter((objectType) =>
        isMigrationObjectTypeEnabled(flags, objectType),
    );
}

export interface InvoicePrerequisiteFlags {
    syncInvoices: boolean | null;
    syncCounterparties: boolean;
    syncCatalogItems: boolean | null;
    syncGeneralLedger: boolean;
    syncChartOfAccounts: boolean;
    isConnectionMigration: boolean;
}

// Invoices link to existing customers and catalog items rather than creating
// them. Historical invoices also need the pre-cutover general ledger, which
// connection migrations only import together with the chart.
export function areInvoicePrerequisitesMet({
    syncInvoices,
    syncCounterparties,
    syncCatalogItems,
    syncGeneralLedger,
    syncChartOfAccounts,
    isConnectionMigration,
}: InvoicePrerequisiteFlags): boolean {
    return (
        !syncInvoices ||
        Boolean(
            syncCounterparties &&
            syncCatalogItems &&
            syncGeneralLedger &&
            (!isConnectionMigration || syncChartOfAccounts),
        )
    );
}

export interface BillPrerequisiteFlags {
    syncBills: boolean | null;
    syncCounterparties: boolean;
    syncGeneralLedger: boolean;
    syncChartOfAccounts: boolean;
    isConnectionMigration: boolean;
}

// Bills link to existing vendors rather than creating them. Historical bills
// also need the pre-cutover general ledger, which connection migrations only
// import together with the chart.
export function areBillPrerequisitesMet({
    syncBills,
    syncCounterparties,
    syncGeneralLedger,
    syncChartOfAccounts,
    isConnectionMigration,
}: BillPrerequisiteFlags): boolean {
    return (
        !syncBills ||
        (syncCounterparties &&
            syncGeneralLedger &&
            (!isConnectionMigration || syncChartOfAccounts))
    );
}

// Invoice and bill lines resolve their posting account through
// `imported_account`, whose `accountId` is only written when accounts mapping
// is finalized. Run before that and every migrated document lands with a blank
// expense/revenue account.
const ACCOUNTS_MAPPING_DEPENDENT_OBJECT_TYPES: ReadonlySet<MigrationObjectType> =
    new Set([MigrationObjectType.INVOICES, MigrationObjectType.BILLS]);

export function isAccountsMappingDependent(
    objectType: MigrationObjectType,
): boolean {
    return ACCOUNTS_MAPPING_DEPENDENT_OBJECT_TYPES.has(objectType);
}

// Catalog items are listed before mapping is finalized, so they run at
// creation and again once it is, when the second idempotent run can resolve
// each item's income account through `imported_account`.
const ACCOUNTS_MAPPING_RERUN_OBJECT_TYPES: ReadonlySet<MigrationObjectType> =
    new Set([MigrationObjectType.CATALOG_ITEMS]);

export function isRerunOnAccountsMapping(
    objectType: MigrationObjectType,
): boolean {
    return ACCOUNTS_MAPPING_RERUN_OBJECT_TYPES.has(objectType);
}
